import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { url } from "../../utils/api";

// Define a service using a base URL and expected endpoints
export const wannaBaeApi = createApi({
  reducerPath: "wannabaeApi",
  tagTypes: [
    "Consultant",
    "Bank",
    "Admin",
    "ReferralCode",
    "ConsultantApplication",
    "User",
  ],
  baseQuery: fetchBaseQuery({
    baseUrl: url,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("authorization");
      if (token) {
        headers.set("Authorization", JSON.parse(token));
      }
      headers.set("Content-Type", "application/json");
      return headers;
    },
  }),

  endpoints: (build) => ({
    getConsultantProfile: build.query({
      query: () => `consultant/profile/`,
      providesTags: ["Consultant"],
    }),
    updateConsultant: build.mutation({
      query: (body) => ({
        url: `consultant/update`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Consultant"],
    }),
    coinRedeem: build.mutation({
      query: (amount) => ({
        url: `coin-redeem/request`,
        method: "POST",
        body: {
          amount: parseInt(amount),
        },
      }),
      invalidatesTags: ["Consultant"],
    }),
    getRedeemHistory: build.query({
      query: ({ status, page = 1, limit = 10 }) =>
        `coin-redeem/redeem-history?status=${status}&limit=${limit}&page=${page}`,
      providesTags: ["Consultant"],
    }),
    getRedeemTransactions: build.query({
      query: ({ status, page = 1, limit = 10 }) =>
        `coin-redeem/transaction?status=${status}&limit=${limit}&page=${page}`,
      providesTags: ["Consultant"],
    }),
    completeRedeemTransaction: build.mutation({
      query: (id) => ({
        url: `coin-redeem/redeem-complete/${id}`,
        method: "PATCH",
      }),
      invalidatesTags: ["Consultant"],
    }),
    createBankAccount: build.mutation({
      query: (body) => ({
        url: `bank-account/create`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Bank"],
    }),
    getBankAccount: build.query({
      query: () => `bank-account/all`,
      providesTags: ["Bank"],
    }),
    deleteBankAccount: build.mutation({
      query: () => ({
        url: `bank-account/delete`,
        method: "DELETE",
      }),
      invalidatesTags: ["Bank"],
    }),
    getConsultantRoomList: build.query({
      query: () => `consultantroom/rooms`,
      providesTags: [""],
    }),
    getMessageByRoomId: build.query({
      query: (roomId) => `consultantmessage/${roomId}`,
      providesTags: [""],
    }),
    updateRoomStatus: build.mutation({
      query: (body) => ({
        url: `consultantroom/update-room-status`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [""],
    }),

    getConsultantList: build.query({
      query: () => ({
        url: `consultant`,
        method: "POST",
      }),
      providesTags: [""],
    }),
    getConsultantByid: build.query({
      query: (id) => `consultant/getById/${id}`,
      providesTags: ["Admin"],
    }),
    updateConsultantByAdmin: build.mutation({
      query: (data) => {
        return {
          url: `consultant/updateById/${data?.id}`,
          method: "PUT",
          body: data?.body,
        };
      },
      invalidatesTags: ["Admin"],
    }),
    getBankAccountByConsultantId: build.query({
      query: (consultantId) =>
        `bank-account/by-id?consultantId=${consultantId}`,
      providesTags: [""],
    }),

    // Referral Code Endpoints
    getReferralCodes: build.query({
      query: () => `referalcode`,
      providesTags: ["ReferralCode"],
    }),
    createReferralCode: build.mutation({
      query: (body) => ({
        url: `referalcode`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ReferralCode"],
    }),
    deleteReferralCode: build.mutation({
      query: (id) => ({
        url: `referalcode/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ReferralCode"],
    }),

    // Consultant Application Endpoints

    submitConsultantApplication: build.mutation({
      query: (body) => ({
        url: `public/consultant-application`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ConsultantApplication"],
    }),

    submitConsultantApplicationByExitingUser: build.mutation({
      query: ({ body, id }) => ({
        url: `public/consultant-application-existing-user?id=${id}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ConsultantApplication"],
    }),

    sendOtpForApplication: build.mutation({
      query: (email) => ({
        url: `public/consultant-application/resend-otp`,
        method: "POST",
        body: { email },
      }),
    }),
    verifyOtpForApplication: build.mutation({
      query: (body) => ({
        url: `public/consultant-application/verify-otp`,
        method: "POST",
        body,
      }),
    }),
    //admin control
    getConsultantApplications: build.query({
      query: ({ status, page = 1, limit = 10 }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        if (status && status.trim() !== "") {
          params.append("status", status);
        }
        console.log({ status });

        return status
          ? `admin/consultant-applications?status=${status}&page=${page}&limit=${limit}`
          : `admin/consultant-applications?page=${page}&limit=${limit}`;
      },
      providesTags: ["ConsultantApplication"],
    }),

    getConsultantApplicationById: build.query({
      query: (id) => `admin/consultant-applications/${id}`,
      providesTags: ["ConsultantApplication"],
    }),

    updateApplicationStatus: build.mutation({
      query: ({ id, status, adminNotes: remarks }) => ({
        url: `admin/consultant-applications/${id}/review`,
        method: "PATCH",
        body: { status, remarks },
      }),
      invalidatesTags: ["ConsultantApplication"],
    }),
    // public/profile?id=66f3b004fd10374eda58a975
    getUserProfileById: build.query({
      query: (id) => `public/profile?id=${id}`,
    }),

    // User Management Endpoints
    getUsersList: build.query({
      query: ({ search = "", page = 1, limit = 10 }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        if (search && search.trim() !== "") {
          params.append("search", search);
        }

        return `Profile/list?${params.toString()}`;
      },
      providesTags: ["User"],
    }),

    getUserProfile: build.query({
      query: (id) => `Profile/${id}`,
      providesTags: ["User"],
    }),

    // Reported Users Endpoints
    getReportedUsers: build.query({
      query: ({ type = "", page = 1, limit = 10 }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        if (type && type.trim() !== "") {
          params.append("type", type);
        }

        return `admin/reported-users?${params.toString()}`;
      },
      providesTags: ["User"],
    }),

    // Get detailed reports for a specific user/consultant
    getDetailedReports: build.query({
      query: ({ type, id }) => `admin/reports/${type}/${id}`,
      providesTags: ["User"],
    }),

    // Block/Unblock User Endpoints
    blockUser: build.mutation({
      query: ({ id, reason }) => ({
        url: `admin/users/${id}/block`,
        method: "PATCH",
        body: { reason },
      }),
      invalidatesTags: ["User"],
    }),

    unblockUser: build.mutation({
      query: (id) => ({
        url: `admin/users/${id}/unblock`,
        method: "PATCH",
      }),
      invalidatesTags: ["User"],
    }),

    // Forgot Password Endpoints
    sendEmailOtp: build.mutation({
      query: (email) => ({
        url: `user/send-email-otp`,
        method: "POST",
        body: { email },
      }),
    }),

    resetPassword: build.mutation({
      query: ({ email, password, otp }) => ({
        url: `user/forgot-password`,
        method: "POST",
        body: { email, password, otp },
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetConsultantProfileQuery,
  useUpdateConsultantMutation,
  useCreateBankAccountMutation,
  useGetBankAccountQuery,
  useDeleteBankAccountMutation,
  useGetConsultantRoomListQuery,
  useLazyGetMessageByRoomIdQuery,
  useUpdateRoomStatusMutation,
  useGetConsultantListQuery,
  useGetConsultantByidQuery,
  useUpdateConsultantByAdminMutation,
  useLazyGetBankAccountByConsultantIdQuery,
  useGetRedeemTransactionsQuery,
  useCompleteRedeemTransactionMutation,
  useCoinRedeemMutation,
  useGetReferralCodesQuery,
  useCreateReferralCodeMutation,
  useDeleteReferralCodeMutation,
  useSendOtpForApplicationMutation,
  useVerifyOtpForApplicationMutation,
  useSubmitConsultantApplicationMutation,
  useGetConsultantApplicationsQuery,
  useGetConsultantApplicationByIdQuery,
  useUpdateApplicationStatusMutation,
  useGetUserProfileByIdQuery,
  useSubmitConsultantApplicationByExitingUserMutation,
  useGetUsersListQuery,
  useGetUserProfileQuery,
  useGetReportedUsersQuery,
  useGetDetailedReportsQuery,
  useBlockUserMutation,
  useUnblockUserMutation,
  useGetRedeemHistoryQuery,
  useSendEmailOtpMutation,
  useResetPasswordMutation,
} = wannaBaeApi;
